// Boomin' — Map screen (/map)

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  GoogleMap,
  OverlayView,
  useJsApiLoader,
} from "@react-google-maps/api";
import {
  BRAND,
  BRAND_SOFT,
  PictX,
  BottomNavX,
  useBoominFx,
} from "./shared";
import { GOOGLE_MAPS_API_KEY } from "../../Constants";
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_LOADER_ID,
  PRESET_AREAS,
  Position,
  RedesignCategory,
  categoryLabel,
  categoryPict,
  couponTag,
  fetchHubsByLatLng,
  fetchTop,
  formatWalk,
  forwardGeocode,
  getCurrentPosition,
  haversineKm,
  inferCategory,
  reverseGeocode,
} from "./api";
import { Coupon, Hub } from "../../Types/App";

const brand = BRAND;
const brandSoft = BRAND_SOFT;

// Conservative initial radius. Map's idle event widens this as the user zooms out.
const INITIAL_RADIUS_KM = 1.5;
const MAX_RADIUS_KM = 50;

type Pin = {
  id: string;
  lat: number;
  lng: number;
  shop: string;
  title: string;
  cat: RedesignCategory;
  catLabel: string;
  walk: string;
  tag: string;
  couponId: number;
};

// Translate zoom level into a search radius (km). Google Maps zoom 14
// shows ~1 city block; zoom 10 shows ~10km. This is a rough heuristic.
const radiusFromZoom = (zoom: number): number => {
  if (zoom >= 16) return 0.5;
  if (zoom >= 15) return 1;
  if (zoom >= 14) return 2;
  if (zoom >= 13) return 4;
  if (zoom >= 12) return 8;
  if (zoom >= 11) return 16;
  if (zoom >= 10) return 30;
  return MAX_RADIUS_KM;
};

const hubsToPins = (hubs: Hub[], origin: Position | null): Pin[] => {
  const pins: Pin[] = [];
  hubs.forEach((h) => {
    (h.coupons || []).forEach((c: Coupon) => {
      const lat = h.latitude;
      const lng = h.longitude;
      if (!lat || !lng) return;
      const km = origin ? haversineKm(origin, { lat, lng }) : null;
      const cat = inferCategory(c, h);
      pins.push({
        id: `${h.id}-${c.id}`,
        lat,
        lng,
        shop: h.name,
        title: c.title,
        cat,
        catLabel: categoryLabel(cat),
        walk: km !== null ? formatWalk(km) : "",
        tag: couponTag(c.period_end),
        couponId: c.id,
      });
    });
  });
  return pins;
};

// Subtle "paper map" styling to match the Boomin' look while keeping
// streets/POIs legible.
const MAP_STYLES: any[] = [
  { elementType: "geometry", stylers: [{ color: "#EFEADD" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#777" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#FFFBF7" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", stylers: [{ visibility: "on" }, { color: "#D7E5C2" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#FFFFFF" }] },
  { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#FFE3D6" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#BDD7E0" }] },
];

export const MapScreen: React.FC = () => {
  useBoominFx();

  const { isLoaded: mapsReady } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [origin, setOrigin] = useState<Position | null>(null);
  const [center, setCenter] = useState<Position | null>(null);
  // myLocation is only set when we have an actual GPS fix from the user.
  // It is intentionally NOT updated for area searches so the blue "you are
  // here" dot never represents anything other than the real current location.
  const [myLocation, setMyLocation] = useState<Position | null>(null);
  const [areaLabel, setAreaLabel] = useState<string>("");
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [popKey, setPopKey] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [toast, setToast] = useState<string>("");
  const mapRef = useRef<google.maps.Map | null>(null);

  // Auto-dismiss the locate/search error pill after a few seconds.
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(""), 3200);
    return () => window.clearTimeout(t);
  }, [toast]);

  const pins = useMemo(() => hubsToPins(hubs, origin), [hubs, origin]);
  const selected = pins.find((p) => p.id === selectedId) || null;

  // Bootstrap: figure out the initial center using geolocation + /top fallback.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let here: Position | null = null;
      try {
        const top = await fetchTop();
        if (!cancelled) here = { lat: top.lat, lng: top.lng };
      } catch {
        /* ignore — use geolocation only */
      }
      try {
        const pos = await getCurrentPosition();
        if (pos.lat && pos.lng) {
          here = pos;
          if (!cancelled) setMyLocation(pos);
        }
      } catch {
        /* user denied — keep IP-based fallback */
      }
      if (cancelled || !here) return;
      setOrigin(here);
      setCenter(here);
      reverseGeocode(here.lat, here.lng).then((label) => {
        if (!cancelled && label) setAreaLabel(label);
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reload = useCallback(async (lat: number, lng: number, radiusKm: number) => {
    try {
      const next = await fetchHubsByLatLng(lat, lng, radiusKm);
      setHubs(next || []);
      // If our selection vanished from the new result, clear it.
      setSelectedId((current) => {
        if (!current) return current;
        const stillThere = (next || []).some((h) =>
          (h.coupons || []).some((c) => `${h.id}-${c.id}` === current)
        );
        return stillThere ? current : null;
      });
    } catch {
      /* swallow; the map stays usable */
    }
  }, []);

  // Once we know where to start, kick off the first load.
  useEffect(() => {
    if (!center) return;
    reload(center.lat, center.lng, INITIAL_RADIUS_KM);
  }, [center, reload]);

  const onPinTap = (id: string) => {
    setSelectedId(id);
    setPopKey((k) => k + 1);
  };

  const onMapLoad = (m: google.maps.Map) => {
    mapRef.current = m;
  };

  const onMapIdle = () => {
    const m = mapRef.current;
    if (!m) return;
    const c = m.getCenter();
    const z = m.getZoom();
    if (!c || z == null) return;
    const lat = c.lat();
    const lng = c.lng();
    reload(lat, lng, radiusFromZoom(z));
  };

  const moveTo = useCallback(
    async (pos: Position, label?: string) => {
      setOrigin(pos);
      setCenter(pos);
      mapRef.current?.panTo({ lat: pos.lat, lng: pos.lng });
      if (label !== undefined) {
        setAreaLabel(label);
      } else {
        const l = await reverseGeocode(pos.lat, pos.lng);
        if (l) setAreaLabel(l);
      }
    },
    []
  );

  const onLocateMe = async () => {
    try {
      const pos = await getCurrentPosition();
      setMyLocation(pos);
      await moveTo(pos);
    } catch (e: any) {
      const denied = e && (e.code === 1 || /denied/i.test(e.message || ""));
      setToast(
        denied
          ? "位置情報の利用が許可されていません。ブラウザの設定をご確認ください。"
          : "現在地を取得できませんでした。"
      );
    }
  };

  const onPickArea = async (pos: Position, label: string) => {
    setPickerOpen(false);
    // Area searches are not "I am here" — drop the blue current-location dot.
    setMyLocation(null);
    await moveTo(pos, label);
  };

  const onPickCurrentLocation = async () => {
    setPickerOpen(false);
    await onLocateMe();
  };

  const onSearchSubmit = async (query: string) => {
    const q = query.trim();
    if (!q) return;
    const preset = PRESET_AREAS.find((a) => a.name === q);
    if (preset) {
      await onPickArea({ lat: preset.lat, lng: preset.lng }, preset.name);
      return;
    }
    const geo = await forwardGeocode(q);
    if (geo) {
      await onPickArea(geo, q);
      return;
    }
    setToast(`「${q}」が見つかりませんでした。`);
  };

  return (
    <>
      <Helmet>
        <title>マップ | Boomin'</title>
      </Helmet>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#E8E4D8",
          overflow: "hidden",
          maxWidth: 640,
          margin: "0 auto",
        }}
      >
        {!GOOGLE_MAPS_API_KEY ? (
          <MapKeyMissing />
        ) : mapsReady && center ? (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={center}
            zoom={15}
            onLoad={onMapLoad}
            onIdle={onMapIdle}
            options={{
              styles: MAP_STYLES,
              disableDefaultUI: true,
              gestureHandling: "greedy",
              clickableIcons: false,
              backgroundColor: "#EFEADD",
            }}
          >
            {myLocation && <UserPosition pos={myLocation} />}
            {pins.map((p) => (
              <OverlayView
                key={`${p.id}-${selectedId === p.id ? popKey : "idle"}`}
                position={{ lat: p.lat, lng: p.lng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={(w, h) => ({
                  x: -(w / 2),
                  y: -h,
                })}
              >
                <PinMarker
                  p={p}
                  selected={selectedId === p.id}
                  onTap={() => onPinTap(p.id)}
                />
              </OverlayView>
            ))}
          </GoogleMap>
        ) : null}

        {/* Top search bar */}
        <div
          style={{
            position: "absolute",
            top: "calc(env(safe-area-inset-top, 0px) + 24px)",
            left: 16,
            right: 16,
            zIndex: 10,
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <a
            href="/"
            aria-label="Boomin' トップ"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "#fff",
              borderRadius: 999,
              padding: "8px 12px 8px 8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              textDecoration: "none",
              color: "#1A1A1A",
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                background: brand,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PictX name="walk" size={13} stroke="#fff" strokeWidth={2} />
            </span>
            Boomin<span style={{ color: brand }}>'</span>
          </a>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            aria-label="エリアを検索"
            style={{
              flex: 1,
              background: "#fff",
              borderRadius: 999,
              padding: "11px 14px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
              border: 0,
              cursor: "pointer",
              textAlign: "left",
              fontFamily: "inherit",
              color: "#444",
            }}
          >
            <PictX name="search" size={16} stroke="#777" strokeWidth={2} />
            <span style={{ fontSize: 13, color: "#444", flex: 1, fontWeight: 500 }}>
              {areaLabel || "現在地周辺"}
            </span>
            <PictX name="sliders" size={16} stroke={brand} strokeWidth={2} />
          </button>
        </div>

        {/* Right column floating buttons */}
        <div
          style={{
            position: "absolute",
            right: 16,
            top: "calc(env(safe-area-inset-top, 0px) + 80px)",
            zIndex: 10,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <button onClick={onLocateMe} style={mapIconBtn} aria-label="現在地">
            <PictX name="locate" size={18} stroke={brand} strokeWidth={1.9} />
          </button>
          <a href="/discover" style={mapIconBtn} aria-label="リスト表示">
            <PictX name="list" size={18} stroke="#1A1A1A" strokeWidth={1.9} />
          </a>
        </div>

        {/* Counter pill */}
        <div
          style={{
            position: "absolute",
            left: 16,
            bottom: 188,
            zIndex: 5,
            background: "#fff",
            borderRadius: 999,
            padding: "7px 13px",
            fontSize: 11,
            fontWeight: 800,
            color: "#1A1A1A",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <span
            data-boomin-anim
            style={{
              width: 7,
              height: 7,
              borderRadius: 999,
              background: brand,
              animation: "boom-pulse 1.4s ease-in-out infinite",
            }}
          />
          このエリアに <span style={{ color: brand }}>{pins.length}</span> 件
        </div>

        {selected && (
          <div
            key={selected.id + popKey + "card"}
            data-boomin-anim
            style={{
              position: "absolute",
              left: 16,
              right: 16,
              bottom: 88,
              zIndex: 8,
              animation: "boom-rise 280ms ease-out",
            }}
          >
            <SelectedCard sel={selected} />
          </div>
        )}

        {toast && (
          <div
            style={{
              position: "absolute",
              top: "calc(env(safe-area-inset-top, 0px) + 76px)",
              left: 16,
              right: 16,
              zIndex: 12,
              background: "#1A1A1A",
              color: "#fff",
              borderRadius: 12,
              padding: "10px 14px",
              fontSize: 12,
              fontWeight: 600,
              boxShadow: "0 8px 22px rgba(0,0,0,0.2)",
              lineHeight: 1.5,
            }}
            role="alert"
          >
            {toast}
          </div>
        )}

        {pickerOpen && (
          <MapAreaSheet
            current={areaLabel}
            onClose={() => setPickerOpen(false)}
            onPickPreset={(p) => onPickArea({ lat: p.lat, lng: p.lng }, p.name)}
            onUseMyLocation={onPickCurrentLocation}
            onSubmitQuery={onSearchSubmit}
          />
        )}
      </div>

      <BottomNavX active="map" brand={brand} />
    </>
  );
};

const mapIconBtn: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 999,
  background: "#fff",
  border: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 12px rgba(0,0,0,0.14)",
  cursor: "pointer",
  flexShrink: 0,
  textDecoration: "none",
};

const UserPosition: React.FC<{ pos: Position }> = ({ pos }) => (
  <OverlayView
    position={pos}
    mapPaneName={OverlayView.OVERLAY_LAYER}
    getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -(h / 2) })}
  >
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: 999,
        background: "#4285F4",
        border: "3px solid #fff",
        boxShadow: "0 0 0 4px rgba(66,133,244,0.25)",
      }}
    />
  </OverlayView>
);

const PinMarker: React.FC<{
  p: Pin;
  selected: boolean;
  onTap: () => void;
}> = ({ p, selected, onTap }) => {
  if (selected) {
    return (
      <button
        onClick={onTap}
        data-boomin-anim
        style={{
          background: "transparent",
          border: 0,
          padding: 0,
          cursor: "pointer",
          animation: "boom-pop 360ms ease-out",
          transformOrigin: "50% 100%",
          position: "relative",
          zIndex: 6,
        }}
      >
        <div
          style={{
            position: "relative",
            background: brand,
            color: "#fff",
            padding: "7px 12px 7px 9px",
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontSize: 11,
            fontWeight: 800,
            whiteSpace: "nowrap",
            boxShadow: `0 8px 20px ${brand}66, 0 2px 4px rgba(0,0,0,0.15)`,
            fontFamily: "inherit",
          }}
        >
          <PictX name={categoryPict(p.cat)} size={14} stroke="#fff" strokeWidth={1.9} />
          {p.shop}
          <span
            style={{
              position: "absolute",
              left: "50%",
              bottom: -7,
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: `7px solid ${brand}`,
            }}
          />
        </div>
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "calc(100% + 4px)",
            transform: "translateX(-50%)",
            width: 8,
            height: 8,
            borderRadius: 999,
            background: brand,
            boxShadow: `0 0 0 6px ${brand}33`,
          }}
        />
      </button>
    );
  }
  return (
    <button
      onClick={onTap}
      data-boomin-anim
      style={{
        background: "transparent",
        border: 0,
        padding: 0,
        cursor: "pointer",
        zIndex: 3,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50% 50% 50% 4px",
          transform: "rotate(-45deg)",
          background: "#fff",
          border: `2px solid ${brand}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ transform: "rotate(45deg)", display: "flex" }}>
          <PictX name={categoryPict(p.cat)} size={16} stroke={brand} strokeWidth={2} />
        </div>
      </div>
    </button>
  );
};

const SelectedCard: React.FC<{ sel: Pin }> = ({ sel }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 18,
      padding: 12,
      display: "flex",
      gap: 12,
      alignItems: "stretch",
      boxShadow: "0 14px 32px rgba(0,0,0,0.18)",
      border: "1px solid rgba(0,0,0,0.04)",
    }}
  >
    <div
      style={{
        width: 64,
        height: 64,
        borderRadius: 12,
        flexShrink: 0,
        background: brandSoft,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <PictX name={categoryPict(sel.cat)} size={30} stroke={brand} strokeWidth={1.7} />
    </div>
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          display: "inline-block",
          fontSize: 9,
          fontWeight: 800,
          color: brand,
          background: brandSoft,
          padding: "2px 7px",
          borderRadius: 999,
          marginBottom: 4,
          alignSelf: "flex-start",
        }}
      >
        {sel.tag}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 800,
          lineHeight: 1.3,
          marginBottom: 3,
          color: "#1A1A1A",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {sel.title}
      </div>
      <div
        style={{
          fontSize: 11,
          color: "#777",
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 100,
          }}
        >
          {sel.shop}
        </span>
        {sel.walk && (
          <>
            <span style={{ width: 1, height: 10, background: "#E5E0D8" }} />
            <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
              <PictX name="walk" size={11} stroke="#777" strokeWidth={1.8} /> {sel.walk}
            </span>
          </>
        )}
      </div>
    </div>
    <a
      href={`/c/${sel.couponId}`}
      style={{
        background: brand,
        color: "#fff",
        border: 0,
        borderRadius: 14,
        padding: "0 14px",
        fontSize: 12,
        fontWeight: 800,
        cursor: "pointer",
        boxShadow: `0 6px 14px ${brand}55`,
        display: "flex",
        alignItems: "center",
        gap: 3,
        flexShrink: 0,
        textDecoration: "none",
      }}
    >
      見る
      <PictX name="chevron" size={14} stroke="#fff" strokeWidth={2.4} />
    </a>
  </div>
);

const MapAreaSheet: React.FC<{
  current: string;
  onClose: () => void;
  onPickPreset: (preset: { name: string; lat: number; lng: number }) => void;
  onUseMyLocation: () => void;
  onSubmitQuery: (q: string) => void;
}> = ({ current, onClose, onPickPreset, onUseMyLocation, onSubmitQuery }) => {
  const [query, setQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || submitting) return;
    setSubmitting(true);
    try {
      await onSubmitQuery(query);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20, 16, 12, 0.45)",
        zIndex: 40,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 640,
          background: "#FFFBF7",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          padding: "18px 20px calc(env(safe-area-inset-bottom, 0px) + 24px)",
        }}
      >
        <div
          style={{
            width: 36,
            height: 4,
            background: "#E5DBD0",
            borderRadius: 999,
            margin: "0 auto 14px",
          }}
        />
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 14,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: "#888",
                fontWeight: 700,
                letterSpacing: "0.08em",
              }}
            >
              エリアを選ぶ
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2 }}>
              {current || "現在地"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            style={{
              background: "transparent",
              border: 0,
              cursor: "pointer",
              color: "#666",
              padding: 4,
            }}
          >
            <PictX name="close" size={20} stroke="#666" strokeWidth={1.8} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#fff",
            border: "1px solid #F0E6DC",
            borderRadius: 14,
            padding: "8px 10px 8px 14px",
            marginBottom: 12,
          }}
        >
          <PictX name="search" size={16} stroke="#777" strokeWidth={2} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="駅・地名で検索 (例: 渋谷)"
            style={{
              flex: 1,
              border: 0,
              outline: "none",
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: 500,
              background: "transparent",
              color: "#1A1A1A",
              padding: "4px 0",
            }}
            autoFocus
          />
          <button
            type="submit"
            disabled={!query.trim() || submitting}
            style={{
              background: brand,
              color: "#fff",
              border: 0,
              borderRadius: 999,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 800,
              cursor: query.trim() && !submitting ? "pointer" : "default",
              opacity: query.trim() && !submitting ? 1 : 0.5,
              fontFamily: "inherit",
            }}
          >
            {submitting ? "..." : "検索"}
          </button>
        </form>

        <button
          type="button"
          onClick={onUseMyLocation}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: brand,
            color: "#fff",
            border: 0,
            borderRadius: 14,
            padding: "12px 14px",
            fontWeight: 800,
            fontSize: 14,
            cursor: "pointer",
            fontFamily: "inherit",
            marginBottom: 12,
          }}
        >
          <PictX name="locate" size={18} stroke="#fff" strokeWidth={2} />
          現在地から探す
        </button>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {PRESET_AREAS.map((preset) => (
            <button
              key={preset.name}
              type="button"
              onClick={() => onPickPreset(preset)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "#fff",
                border: "1px solid #F0E6DC",
                borderRadius: 14,
                padding: "12px 14px",
                cursor: "pointer",
                color: "#1A1A1A",
                fontFamily: "inherit",
                textAlign: "left",
              }}
            >
              <PictX name="pin" size={16} stroke={brand} strokeWidth={2} />
              <span style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>
                {preset.name}
              </span>
              <PictX name="chevron" size={14} stroke="#999" strokeWidth={2} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Fallback when REACT_APP_GOOGLE_MAPS_API_KEY isn't set — never expected in
// production, but keeps the screen rendering during local development.
const MapKeyMissing: React.FC = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      textAlign: "center",
      color: "#666",
      fontSize: 13,
      lineHeight: 1.7,
    }}
  >
    Google Maps APIキーが設定されていません。
    <br />
    .env の REACT_APP_GOOGLE_MAPS_API_KEY を確認してください。
  </div>
);

export default MapScreen;
